/** The paper tone beside light and deep, and the untouched grey on each ground. */
import React, { useCallback, useRef } from 'react';
import { FmHeading, FmStack, FmText } from './FmComposition';
import { FmGalleryIdentity } from './FmGalleryIdentity';
import { FmContentWidth } from './FmLayout';
import { FmSurface } from './FmSurface';
import { FmValueList } from './FmValueList';
import { FmSection } from './sections/FmSection';
import type { FmSection as FmSectionDescriptor, FmSectionTone } from './sections/section.types';
import {
  contrastRatio,
  effectiveBackground,
  parseComputedColor,
  resolveRoleColor,
} from './fmContrast';
import {
  GRAPHIC,
  TEXT_AA,
  useMeasured,
  valueItems,
  type FmSurfaceGalleryCopy,
  type Measured,
} from './surfaceGalleryMeasure';

const SPECIMEN_TONES: ReadonlyArray<'light' | 'paper' | 'deep'> = ['light', 'paper', 'deep'];
/** Gallery review chrome: the one job of each tone. */
const TONE_JOBS: Record<'light' | 'paper' | 'deep', string> = {
  light: 'Reading: copy, charts and maps.',
  paper: 'Working evidence: tables, ledgers and forms, under a hairline seam.',
  deep: 'The statement: the answer and the hand-off.',
};
const GROUNDS: ReadonlyArray<'paper' | 'imagery' | 'deep'> = ['paper', 'imagery', 'deep'];
/** The role each ground resolves the untouched grey to. */
const UNTOUCHED_ROLE: Record<'paper' | 'imagery' | 'deep', string> = {
  paper: '--fm-operation-untouched-on-light',
  imagery: '--fm-operation-untouched-on-dark',
  deep: '--fm-operation-untouched-on-dark',
};

function bandSection(section: FmSectionDescriptor, tone: FmSectionTone): FmSectionDescriptor {
  return { ...section, id: `${section.id}-${tone}`, tone };
}

export const ToneBands: React.FC<{ evidence: FmSectionDescriptor; copy: FmSurfaceGalleryCopy }> = ({
  evidence,
  copy,
}) => {
  const host = useRef<HTMLDivElement>(null);
  const measure = useCallback(() => {
    const root = host.current;
    if (!root) return null;
    return SPECIMEN_TONES.flatMap((tone) => {
      const band = root.querySelector<HTMLElement>(`.fm-section-band--${tone}`);
      if (!band) return [];
      const ground = effectiveBackground(band);
      const row = (role: 'text' | 'muted', token: string, label: string): Measured => ({
        id: `${tone}-${role}`,
        label: `${copy.tones[tone]} · ${label}`,
        ratio: contrastRatio(resolveRoleColor(band, token), ground),
        min: TEXT_AA,
      });
      return [row('text', '--fm-d-fg', copy.textRole), row('muted', '--fm-d-fg-2', copy.mutedRole)];
    });
  }, [copy]);
  const measured = useMeasured(measure);
  return (
    <div ref={host} data-testid="surface-paper-bands">
      {SPECIMEN_TONES.map((tone) => (
        <div key={tone} data-testid={`surface-band-${tone}`}>
          <FmContentWidth width="content" padY="sm">
            <FmGalleryIdentity
              vocabulary="tone"
              names={[tone]}
              label={copy.tones[tone]}
              useFor={TONE_JOBS[tone]}
              data-testid={`surface-band-${tone}-identity`}
            />
          </FmContentWidth>
          <FmSection section={bandSection(evidence, tone)} headingLevel={4} />
        </div>
      ))}
      <FmContentWidth width="content" padY="sm">
        <FmStack gap="xs">
          <FmHeading level={4}>{copy.bandsTitle}</FmHeading>
          <FmValueList
            items={valueItems(copy, measured, 'surface-band')}
            density="compact"
            data-testid="surface-band-contrast"
          />
        </FmStack>
      </FmContentWidth>
    </div>
  );
};

export const UntouchedGrounds: React.FC<{ copy: FmSurfaceGalleryCopy }> = ({ copy }) => {
  const host = useRef<HTMLDivElement>(null);
  const measure = useCallback(() => {
    const root = host.current;
    if (!root) return null;
    return GROUNDS.flatMap((ground) => {
      const swatch = root.querySelector<HTMLElement>(`[data-untouched-swatch="${ground}"]`);
      const behind = swatch?.parentElement;
      if (!swatch || !behind) return [];
      const fill = parseComputedColor(getComputedStyle(swatch).backgroundColor);
      return [
        {
          id: ground,
          label: `${copy.grounds[ground]} · ${UNTOUCHED_ROLE[ground]}`,
          ratio: contrastRatio(fill, effectiveBackground(behind)),
          min: GRAPHIC,
        },
      ];
    });
  }, [copy]);
  const measured = useMeasured(measure);
  const swatch = (ground: 'paper' | 'imagery' | 'deep') => (
    <div className="fm-surface-gallery__swatch-row">
      <span
        className="fm-surface-gallery__swatch"
        data-untouched-swatch={ground}
        aria-hidden="true"
      />
      <span>{copy.untouchedLabel}</span>
    </div>
  );
  return (
    <FmContentWidth width="content" padY="sm">
      <FmStack gap="sm">
        <FmHeading level={4}>{copy.untouchedTitle}</FmHeading>
        <div
          ref={host}
          className="fm-surface-gallery__grounds"
          data-testid="surface-untouched-grounds"
        >
          {GROUNDS.map((ground) =>
            ground === 'deep' ? (
              <div key={ground} className="fm-surface-gallery__ground" data-ground="deep-stage">
                <FmSurface
                  surface="glass-deep"
                  padding="compact"
                  data-testid="surface-untouched-deep"
                >
                  <FmText size="caption">{copy.grounds.deep}</FmText>
                  {swatch('deep')}
                </FmSurface>
              </div>
            ) : (
              <div
                key={ground}
                className="fm-surface-gallery__ground"
                data-fm-map-ground={ground}
                data-testid={`surface-untouched-${ground}`}
              >
                <FmText size="caption">{copy.grounds[ground]}</FmText>
                {swatch(ground)}
              </div>
            ),
          )}
        </div>
        <FmValueList
          items={valueItems(copy, measured, 'surface-untouched')}
          density="compact"
          data-testid="surface-untouched-contrast"
        />
      </FmStack>
    </FmContentWidth>
  );
};
